import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import ReactECharts from 'echarts-for-react';
import { TAppReactCharts } from './types';
import { useLazyGetFieldDetailsQuery } from '../../services/mVPGraphQlService';
import { ChartFieldDetails } from './chartFieldDetails';
import { Box, Grid } from '@mui/material';
import './index.scss';

export const AppReactECharts: React.FC<TAppReactCharts> = ({
  startTime,
  endTime,
  commonFilters,
  fieldNumber,
  plotType,
  setSowingTime,
  setHarvestTime,
  onNavigate,
  selectedIndex,
  ...restProps
}) => {
  const [
    getFieldDetails,
    { data: fieldTableData, isFetching },
  ] = useLazyGetFieldDetailsQuery();
  const [indexValue, setIndexValue] = useState<number>();
  const [fieldValues, setFieldValues] = useState();
  const getFieldDetailsByFieldNo = (
    fieldNumber: string | number
  ) => {
    getFieldDetails({
      fieldNumbers: [fieldNumber],
      startTime: startTime || 0,
      endTime: endTime || 0,
      commonFilters,
    });
  };

  useEffect(() => {
    const fieldDetails =
      fieldTableData?.data?.GetFieldDetailsForFieldNumbers
        ?.fieldDetails?.[0];
    setFieldValues(fieldDetails);
    if (fieldDetails?.fieldDetails) {
      setSowingTime?.(
        fieldDetails?.fieldDetails?.sowingTime?.seconds
          ? {
              seconds:
                fieldDetails?.fieldDetails?.sowingTime
                  .seconds,
            }
          : null
      );
      setHarvestTime?.(
        fieldDetails?.fieldDetails?.harvestTime?.seconds
          ? {
              seconds:
                fieldDetails?.fieldDetails.harvestTime
                  .seconds,
            }
          : null
      );
    }
  }, [fieldTableData, setSowingTime, setHarvestTime]);

  const onChartClick = useCallback(
    (e: { name: string; data: number }) => {
      plotType === 'scatter' && setIndexValue(e.data);
      !fieldNumber && getFieldDetailsByFieldNo(e.name);
    },
    []
  );

  const onChartEvents = {
    click: onChartClick,
  };

  const fieldDetailsDataSet =
    fieldTableData?.data?.GetFieldDetailsForFieldNumbers
      ?.fieldDetails;
  const fieldDetails = fieldDetailsDataSet?.length
    ? fieldDetailsDataSet[0]?.fieldDetails
    : null;

  useEffect(() => {
    if (fieldNumber) {
      getFieldDetailsByFieldNo(fieldNumber);
    }
  }, [fieldNumber]);

  const [isShowDetails, setIsShowDetails] =
    useState<boolean>(true);

  const handleOnCancel = () => {
    setIsShowDetails(!isShowDetails);
  };

  const isFieldDetailsVisible =
    isShowDetails && fieldDetails;

  const chartRef = useRef<ReactECharts>(null);

  // Effect to handle chart resize when isFieldDetailsVisible changes
  useEffect(() => {
    const chartInstance =
      chartRef.current?.getEchartsInstance();
    if (chartInstance) {
      chartInstance.resize();
    }
  }, [isFieldDetailsVisible]);

  return (
    <>
      <Grid container>
        <Grid
          item
          xs={12}
          sm={12}
          md={isFieldDetailsVisible ? 9 : 12}
          lg={isFieldDetailsVisible ? 9 : 12}>
          <ReactECharts
            ref={chartRef}
            {...restProps}
            onEvents={onChartEvents}
            style={{ height: '400px', width: '100%' }}
          />
        </Grid>
        {!isShowDetails && fieldDetails && (
          <Box
            onClick={handleOnCancel}
            className="show-field-details-btn">
            Show field details
          </Box>
        )}
        {isFieldDetailsVisible &&
          plotType != 'timeSeriesPlot' && (
            <Grid item xs={12} sm={12} md={3} lg={3} mt={4}>
              <ChartFieldDetails
                fieldDetails={{
                  ...fieldDetails,
                  index: plotType
                    ? selectedIndex || commonFilters?.index
                    : undefined,
                  indexValue,
                }}
                className="col-span-3"
                handleOnCancel={handleOnCancel}
                isLoading={isFetching}
                plotType={plotType}
                onNavigate={onNavigate}
                fieldNumber={fieldValues?.fieldNumber}
              />
            </Grid>
          )}
      </Grid>
    </>
  );
};
